import React, { FC, memo, ReactNode, useMemo } from 'react';
import { useStyles } from './MyTeamEvalReport.styles';
import { DataState } from '@shared/enums/DataState';
import { AsyncData } from '@shared/interfaces/asyncData';
import { DataErrorView } from '@shared/components/DataErrorView';
import { MyTeamEvalData } from '@modules/EvaluationModule/interfaces/MyTeamEvalData';
import { InfoView } from '@shared/components/InfoView/InfoView';
import EmptyEval from '@assets/img/EmptyEval.png';
import { EvaluationInfoViewTexts } from '@modules/EvaluationModule/enums/EvaluationInfoViewTexts';
import { EvalStatuses } from '@modules/EvaluationModule/pages/MyPeopleEvalPage/enums/EvalStatuses';

const STATUS_EMPTY_MESSAGE: Partial<Record<EvalStatuses, string>> = {
  [EvalStatuses.All]: EvaluationInfoViewTexts.NoEvaluations,
  [EvalStatuses.Upcoming]: EvaluationInfoViewTexts.NoUpcomingEvaluations,
  [EvalStatuses.Ongoing]: EvaluationInfoViewTexts.NoOngoingEvaluations,
  [EvalStatuses.PendingToClose]: EvaluationInfoViewTexts.NoPendingToCloseEvaluations,
  [EvalStatuses.PendingToStart]: EvaluationInfoViewTexts.NoPendingToStartEvaluations,
  [EvalStatuses.Closed]: EvaluationInfoViewTexts.NoClosedEvaluations,
};

interface Props {
  tableTitle: string;
  myTeamEvalData: AsyncData<MyTeamEvalData[]>;
  children: ReactNode;
  header?: ReactNode;
  sideBlock?: ReactNode;
  statusFilter?: EvalStatuses[];
  count?: number | null;
}

const MyTeamEvalReportComponent: FC<Props> = ({
  tableTitle,
  myTeamEvalData,
  children,
  header,
  sideBlock,
  statusFilter,
  count,
}) => {
  const styles = useStyles();
  const isDataLoading = myTeamEvalData.state === DataState.Pending;

  const notFoundMessage = useMemo(() => {
    const singleStatus = statusFilter?.length === 1 ? statusFilter[0] : null;
    return (singleStatus && STATUS_EMPTY_MESSAGE[singleStatus]) ?? EvaluationInfoViewTexts.NoEvaluations;
  }, [statusFilter]);

  return myTeamEvalData.state === DataState.Rejected ? (
    <DataErrorView error={myTeamEvalData.error} />
  ) : (
    <div className={styles.root}>
      {header && header}

      <div className={styles.tableContainer}>
        <div className={styles.header}>
          <div className={styles.title}>
            {tableTitle}
            {!isDataLoading && count != null ? ` (${count})` : ''}
            {sideBlock}
          </div>
        </div>
        {!isDataLoading && myTeamEvalData.data && myTeamEvalData.data?.length < 1 ? (
          <InfoView icon={EmptyEval} text={notFoundMessage} inComponent />
        ) : (
          children
        )}
      </div>
    </div>
  );
};

export const MyTeamEvalReport = memo(MyTeamEvalReportComponent);
